/* eslint-disable */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import '../../table.css';
import '../../styles/components.css';

interface Company {
  id: string;
  name: string;
  email?: string;
  address?: any;
  csr_contact?: any;
  logo_url?: string;
  subscription_status?: string;
}

interface Subscription {
  id: string;
  company_id: string;
  plan_id: string;
  status: string;
  billing_cycle: string;
  current_period_start: string;
  current_period_end: string;
  company_name?: string;
  plan_name?: string;
  plan_price?: number;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  price_monthly: number;
  price_yearly: number;
  features: any;
  is_active: boolean;
}

interface DocRepo {
  id: string;
  category: string;
  role: string;
  step_number: number;
  display_order?: number;
  document_name: string;
  action_type: string;
  description: string;
  responsibility: string;
  can_company_upload: boolean;
  can_ngo_upload: boolean;
  visible_to_company: boolean;
  visible_to_ngo: boolean;
  guiding_doc_url?: string;
  guiding_doc_filename?: string;
  is_active?: boolean;
  due_date?: string | null;
}

const AdminDashboard: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [docRepo, setDocRepo] = useState<DocRepo[]>([]);
  const [usersList, setUsersList] = useState<any[]>([]);
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);
  const [logoErrors, setLogoErrors] = useState<Record<string, boolean>>({});
  const [signedLogoUrls, setSignedLogoUrls] = useState<Record<string, string>>({});
  const [guidelineUploading, setGuidelineUploading] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'companies' | 'documents'>('companies');
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState<DocRepo | null>(null);
  const [docFilter, setDocFilter] = useState<'all' | 'company' | 'ngo' | 'foundation'>('all');
  const [newDocument, setNewDocument] = useState({
    document_name: '',
    description: '',
    category: 'compliance',
    role: 'company' as 'company' | 'ngo' | 'foundation',
    step_number: 1,
    display_order: 1,
    action_type: 'upload',
    responsibility: '',
    can_company_upload: false,
    can_ngo_upload: false,
    visible_to_company: true,
    visible_to_ngo: true,
    due_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch companies
      const { data: companiesData } = await supabase
        .from('companies')
        .select('id, name, csr_contact, logo_url')
        .order('name', { ascending: true });
        
      setCompanies(companiesData || []);

      // Fetch signed urls for logos
      if (companiesData) {
        const urls: Record<string, string> = {};
        for (const company of companiesData) {
          if (company.logo_url && !company.logo_url.startsWith('http')) {
             try {
                const decodedPath = decodeURIComponent(company.logo_url);
                const { data } = await supabase.storage.from('company-documents').createSignedUrl(decodedPath, 3600);
                if (data?.signedUrl) {
                    urls[company.logo_url] = data.signedUrl;
                }
             } catch (e) {
                console.error("Error signing logo url", e);
             }
          }
        }
        setSignedLogoUrls(urls);
      }

      // Fetch subscriptions with company and plan details
      const { data: subscriptionsData } = await supabase
        .from('company_subscriptions')
        .select(`
          id,
          company_id,
          plan_id,
          status,
          billing_cycle,
          current_period_start,
          current_period_end,
          companies!inner(name),
          subscription_plans!inner(name, price_monthly, price_yearly)
        `)
        .order('current_period_end', { ascending: false });
      
      if (subscriptionsData) {
        const formattedSubscriptions = subscriptionsData.map((sub: any) => ({
          id: sub.id,
          company_id: sub.company_id,
          plan_id: sub.plan_id,
          status: sub.status,
          billing_cycle: sub.billing_cycle,
          current_period_start: sub.current_period_start,
          current_period_end: sub.current_period_end,
          company_name: sub.companies?.name || 'Unknown',
          plan_name: sub.subscription_plans?.name || 'Unknown',
          plan_price: sub.billing_cycle === 'monthly' 
            ? sub.subscription_plans?.price_monthly 
            : sub.subscription_plans?.price_yearly
        }));
        setSubscriptions(formattedSubscriptions);
      }

      // Fetch subscription plans
      const { data: plansData } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('price_monthly');
      setSubscriptionPlans(plansData || []);

      // Fetch document repository
      const { data: docRepoData } = await supabase
        .from('document_repository')
        .select('*')
        .order('display_order')
        .order('step_number');
      setDocRepo(docRepoData || []);

      // Fetch users for user management
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('*')
        .order('role');
      setUsersList(profilesData || []);

      // Fetch company_users to map profiles to companies
      const { data: compUsersData } = await supabase
        .from('company_users')
        .select('*');
      setCompanyUsers(compUsersData || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Handle upload guideline doc
  const handleGuidelineUpload = async (docId: string, file: File) => {
    setGuidelineUploading(docId);
    const path = `document-repo-guidelines/${docId}_${Date.now()}_${file.name}`;
    const { error: uploadError } = await supabase.storage
      .from('general-documents')
      .upload(path, file);
    if (uploadError) {
      setError(uploadError.message);
      setGuidelineUploading('');
      return;
    }
    const { data } = supabase.storage.from('general-documents').getPublicUrl(path);
    await supabase.from('document_repository')
      .update({ guiding_doc_url: data.publicUrl, guiding_doc_filename: file.name })
      .eq('id', docId);
    await fetchData();
    setGuidelineUploading('');
  };

  // Handle role/view/edit toggles
  const updateRepoField = async (docId: string, field: string, value: any) => {
    await supabase.from('document_repository').update({ [field]: value }).eq('id', docId);
    await fetchData();
  };

  // Handle add/edit document
  const handleSaveDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    const isCompanyOrFoundation = newDocument.role === 'company' || newDocument.role === 'foundation';
    try {
      if (editingDocument) {
        // Update existing document
        const { error } = await supabase
          .from('document_repository')
          .update({
            document_name: newDocument.document_name,
            description: newDocument.description,
            category: newDocument.category,
            role: newDocument.role,
            step_number: newDocument.step_number,
            display_order: newDocument.display_order,
            action_type: newDocument.action_type,
            responsibility: newDocument.responsibility,
            can_company_upload: isCompanyOrFoundation,
            can_ngo_upload: !isCompanyOrFoundation,
            visible_to_company: true,
            visible_to_ngo: true,
            due_date: newDocument.due_date || null
          })
          .eq('id', editingDocument.id);
        if (error) throw error;
      } else {
        // Create new document
        const { error } = await supabase
          .from('document_repository')
          .insert({
            document_name: newDocument.document_name,
            description: newDocument.description,
            category: newDocument.category,
            role: newDocument.role,
            step_number: newDocument.step_number,
            display_order: newDocument.display_order,
            action_type: newDocument.action_type,
            responsibility: newDocument.responsibility,
            can_company_upload: isCompanyOrFoundation,
            can_ngo_upload: !isCompanyOrFoundation,
            visible_to_company: true,
            visible_to_ngo: true,
            is_active: true,
            due_date: newDocument.due_date || null
          });
        if (error) throw error;
      }
      setShowAddDocumentModal(false);
      setEditingDocument(null);
      setNewDocument({
        document_name: '',
        description: '',
        category: 'compliance',
        role: 'company',
        step_number: 1,
        display_order: 1,
        action_type: 'upload',
        responsibility: '',
        can_company_upload: false,
        can_ngo_upload: false,
        visible_to_company: true,
        visible_to_ngo: true,
        due_date: ''
      });
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to save document');
    }
  };

  // Handle delete document
  const handleDeleteDocument = async (docId: string) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) return;
    try {
      const { error } = await supabase
        .from('document_repository')
        .delete()
        .eq('id', docId);
      if (error) throw error;
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete document');
    }
  };

  // Calculate statistics
  const handleViewDoc = async (e: React.MouseEvent, url: string, download: boolean = false) => {
    e.preventDefault();
    if (!url) return;
    
    if (!url.includes('/storage/v1/object/public/')) {
        window.open(url, '_blank');
        return;
    }
    
    try {
        const parts = url.split('/storage/v1/object/public/');
        const afterPublic = parts[1];
        const pathParts = afterPublic.split('/');
        
        if (pathParts.length > 1) {
            const bucketName = pathParts[0];
            const pathInBucket = pathParts.slice(1).join('/');
            
            const { data } = await supabase.storage.from(bucketName).createSignedUrl(pathInBucket, 60 * 60, {
                download: download
            });
            
            if (data && data.signedUrl) {
                if (download) {
                   const a = document.createElement('a');
                   a.href = data.signedUrl;
                   a.download = '';
                   document.body.appendChild(a);
                   a.click();
                   document.body.removeChild(a);
                } else {
                   window.open(data.signedUrl, '_blank');
                }
                return;
            }
        }
        window.open(url, '_blank');
    } catch (err) {
        window.open(url, '_blank');
    }
  };

  const stats = {
    totalCompanies: companies.length,
    activeSubscriptions: subscriptions.filter(s => s.status === 'active').length,
    totalRevenue: subscriptions
      .filter(s => s.status === 'active')
      .reduce((sum, s) => sum + (s.plan_price || 0), 0),
    monthlyRevenue: subscriptions
      .filter(s => s.status === 'active' && s.billing_cycle === 'monthly')
      .reduce((sum, s) => sum + (s.plan_price || 0), 0),
    yearlyRevenue: subscriptions
      .filter(s => s.status === 'active' && s.billing_cycle === 'yearly')
      .reduce((sum, s) => sum + (s.plan_price || 0), 0),
    totalDocuments: docRepo.length
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 2rem', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem', marginTop: 0 }}>
          Admin Dashboard
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
          Manage companies, subscriptions, and compliance documents
        </p>
      </div>

      {error && (
        <div className="toast toast-error" style={{ marginBottom: '1.5rem' }}>
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid var(--border-light)' }}>
        <button
          onClick={() => setActiveTab('companies')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: 'transparent',
            color: activeTab === 'companies' ? 'var(--primary)' : 'var(--text-secondary)',
            borderBottom: activeTab === 'companies' ? '2px solid var(--primary)' : '2px solid transparent',
            cursor: 'pointer',
            fontWeight: activeTab === 'companies' ? 600 : 400,
            fontSize: '0.95rem'
          }}
        >
          Clients & Users
        </button>
        <button
          onClick={() => setActiveTab('documents')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: 'transparent',
            color: activeTab === 'documents' ? 'var(--primary)' : 'var(--text-secondary)',
            borderBottom: activeTab === 'documents' ? '2px solid var(--primary)' : '2px solid transparent',
            cursor: 'pointer',
            fontWeight: activeTab === 'documents' ? 600 : 400,
            fontSize: '0.95rem'
          }}
        >
          Compliance Repository ({stats.totalDocuments})
        </button>
      </div>



      {/* Clients & Users Tab */}
      {activeTab === 'companies' && (
        <section>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', margin: 0 }}>
              <span style={{ fontSize: '1.25rem' }}>🏢</span> Select Client
            </h2>
            <p style={{ color: 'var(--text-secondary)', margin: '0.5rem 0 0 0' }}>Choose a client to access their dashboard and data.</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '2rem', marginBottom: '4rem' }}>
            {companies.map(c => (
              <div 
                key={c.id}
                className="card"
                onClick={() => {
                    localStorage.setItem('admin_impersonated_company_id', c.id);
                    window.location.href = '/client/company/dashboard';
                }}
                style={{ 
                    padding: '2rem', 
                    textAlign: 'center', 
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
                }}
              >
                <div style={{ 
                    width: '80px', 
                    height: '80px', 
                    borderRadius: '50%', 
                    backgroundColor: '#f3f4f6', 
                    margin: '0 auto 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem',
                    color: 'var(--primary)',
                    overflow: 'hidden'
                }}>
                  {(!logoErrors[c.id] && c.logo_url && (c.logo_url.startsWith('http') || signedLogoUrls[c.logo_url])) ? (
                    <img 
                        src={c.logo_url.startsWith('http') ? c.logo_url : signedLogoUrls[c.logo_url]} 
                        alt={c.name} 
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                        onError={() => {
                            setLogoErrors(prev => ({ ...prev, [c.id]: true }));
                        }}
                    />
                  ) : (
                    c.name.charAt(0).toUpperCase()
                  )}
                </div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text)' }}>{c.name}</h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                  {(() => {
                    const link = companyUsers.find((cu: any) => cu.company_id === c.id);
                    if (link) {
                        const userProfile = usersList.find((u: any) => u.id === link.user_id);
                        return userProfile?.email || c.csr_contact?.email || 'No email provided';
                    }
                    return c.csr_contact?.email || 'No email provided';
                  })()}
                </p>
              </div>
            ))}
          </div>


        </section>
      )}



      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600 }}>Compliance Repository Management</h2>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <select
                value={docFilter}
                onChange={(e) => setDocFilter(e.target.value as any)}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Roles</option>
                <option value="company">Company</option>
                <option value="ngo">NGO</option>
                <option value="foundation">Foundation</option>
              </select>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setEditingDocument(null);
                  setNewDocument({
                    document_name: '',
                    description: '',
                    category: 'compliance',
                    role: 'company',
                    step_number: docRepo.length > 0 ? Math.max(...docRepo.map(d => d.step_number || 0)) + 1 : 1,
                    display_order: docRepo.length > 0 ? Math.max(...docRepo.map(d => d.display_order || 0)) + 1 : 1,
                    action_type: 'upload',
                    responsibility: '',
                    can_company_upload: false,
                    can_ngo_upload: false,
                    visible_to_company: true,
                    visible_to_ngo: true,
                    due_date: ''
                  });
                  setShowAddDocumentModal(true);
                }}
              >
                + Add New Document
              </button>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Document Name</th>
                  <th>Role</th>
                  <th>Step #</th>
                  <th>Due Date</th>
                  <th>Guideline</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {docRepo.filter(doc => docFilter === 'all' || doc.role === docFilter).map(doc => (
                  <tr key={doc.id}>
                    <td style={{ fontWeight: 600 }}>{doc.document_name}</td>
                    <td>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        backgroundColor: doc.role === 'company' ? '#3b82f615' : (doc.role === 'foundation' ? '#8b5cf615' : '#10b98115'),
                        color: doc.role === 'company' ? '#3b82f6' : (doc.role === 'foundation' ? '#8b5cf6' : '#10b981'),
                        fontWeight: 600
                      }}>
                        {doc.role?.charAt(0).toUpperCase() + doc.role?.slice(1) || 'N/A'}
                      </span>
                    </td>
                    <td>{doc.display_order || doc.step_number}</td>
                    <td>
                      {doc.due_date ? new Date(doc.due_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td style={{ minWidth: 170 }}>
                      {doc.guiding_doc_url ? (
                        <div>
                          <button onClick={(e) => handleViewDoc(e, doc.guiding_doc_url!)} style={{ fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block', background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                            {doc.guiding_doc_filename || 'Download'}
                          </button>
                        </div>
                      ) : null}
                      <input
                        type="file"
                        style={{ display: 'block', marginTop: 6, fontSize: '0.75rem' }}
                        onChange={e => e.target.files && e.target.files[0] && handleGuidelineUpload(doc.id, e.target.files[0])}
                      />
                      {guidelineUploading === doc.id && (<span className="spinner" style={{ marginLeft: 8, verticalAlign: 'middle' }} />)}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => {
                            setEditingDocument(doc);
                            setNewDocument({
                              document_name: doc.document_name,
                              description: doc.description || '',
                              category: doc.category || 'compliance',
                              role: doc.role as 'company' | 'ngo' | 'foundation',
                              step_number: doc.step_number || 1,
                              display_order: doc.display_order || doc.step_number || 1,
                              action_type: doc.action_type || 'upload',
                              responsibility: doc.responsibility || '',
                              can_company_upload: doc.can_company_upload || false,
                              can_ngo_upload: doc.can_ngo_upload || false,
                              visible_to_company: doc.visible_to_company || false,
                              visible_to_ngo: doc.visible_to_ngo || false,
                              due_date: doc.due_date || ''
                            });
                            setShowAddDocumentModal(true);
                          }}
                          style={{ fontSize: '0.75rem' }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm"
                          onClick={() => handleDeleteDocument(doc.id)}
                          style={{
                            fontSize: '0.75rem',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Add/Edit Document Modal */}
      {showAddDocumentModal && (
        <div className="modal-overlay" onClick={() => {
          setShowAddDocumentModal(false);
          setEditingDocument(null);
        }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{
              marginBottom: '1.5rem',
              paddingBottom: '1rem',
              borderBottom: '2px solid var(--border-light)'
            }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: 'var(--text)',
                margin: 0,
                marginBottom: '0.25rem'
              }}>
                {editingDocument ? 'Edit Document' : 'Add New Document'}
              </h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                {editingDocument ? 'Update document details' : 'Create a new compliance document template'}
              </p>
            </div>
            <form onSubmit={handleSaveDocument}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Document Name *
                  </label>
                  <input
                    type="text"
                    value={newDocument.document_name}
                    onChange={(e) => setNewDocument({ ...newDocument, document_name: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem'
                    }}
                    placeholder="e.g., Registration Certificate"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Description
                  </label>
                  <textarea
                    value={newDocument.description}
                    onChange={(e) => setNewDocument({ ...newDocument, description: e.target.value })}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem',
                      resize: 'vertical'
                    }}
                    placeholder="Describe what this document is for..."
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                      Category *
                    </label>
                    <select
                      value={newDocument.category}
                      onChange={(e) => setNewDocument({ ...newDocument, category: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '0.875rem'
                      }}
                    >
                      <option value="compliance">Compliance</option>
                      <option value="legal">Legal</option>
                      <option value="financial">Financial</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                      Role *
                    </label>
                    <select
                      value={newDocument.role}
                      onChange={(e) => setNewDocument({ ...newDocument, role: e.target.value as 'company' | 'ngo' | 'foundation' })}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '0.875rem'
                      }}
                    >
                      <option value="company">Company</option>
                      <option value="ngo">NGO</option>
                      <option value="foundation">Foundation</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                      Display Order *
                    </label>
                    <input
                      type="number"
                      value={newDocument.display_order}
                      onChange={(e) => setNewDocument({ ...newDocument, display_order: parseInt(e.target.value) || 1 })}
                      required
                      min={1}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={newDocument.due_date || ''}
                      onChange={(e) => setNewDocument({ ...newDocument, due_date: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setShowAddDocumentModal(false);
                    setEditingDocument(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {editingDocument ? 'Update Document' : 'Create Document'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;

